
import { RevButton } from '@ds/components/Button'
import { RevIllustration } from '@ds/components/Illustration'
import { RevSkeleton } from '@ds/components/Skeleton'
import algoliasearch from 'algoliasearch/lite'
import isEmpty from 'lodash/isEmpty'
import { mapGetters } from 'vuex'

import logger from '@logger'
import { trackClick } from '@tracking/events'

import Chart from '../Chart/Chart.vue'
import { groupBy } from '../Table/Table.helpers'
import Table from '../Table/Table.vue'

import translations from './ComparisonResults.translations'

export default {
  components: {
    RevButton,
    Chart,
    RevIllustration,
    RevSkeleton,
    Table,
  },

  props: {
    comparisonConfig: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    selectedModels: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      models: [],
    }
  },

  async fetch() {
    try {
      this.models = await Promise.all(
        this.selectedModels.map((model) => this.getModelData(model)),
      )
    } catch (error) {
      this.$nuxt.error({
        statusCode: 404,
        message: error.message,
      })
    }
  },

  watch: {
    selectedModels: '$fetch',
  },

  computed: {
    ...mapGetters({ locale: 'config/locale' }),

    translations: () => translations,

    algoliaClient() {
      return algoliasearch(this.$config.ALGOLIA_ID, this.$config.ALGOLIA_KEY)
    },

    hasModels() {
      return !isEmpty(this.models)
    },

    modelsFormatted() {
      const modelsFormatted = this.models.map(({ model, offer }) => {
        // Pick the model link for the current locale
        const modelLinkForCurrentLocale = Object.entries(
          model?.model.links,
        ).find(([index]) => index === this.locale)
        const fallBackModelLink = !isEmpty(modelLinkForCurrentLocale)
          ? modelLinkForCurrentLocale[1]
          : null

        return {
          // Use `offer` image in priority, and `model` image as fallback
          image: offer?.image1 || model?.model.image || null,
          hasImage: !isEmpty(offer?.image1) || !isEmpty(model?.model.image),
          name: model?.model.name,
          price: offer?.price,
          currency: offer?.currency,
          // Use `offer` link in priority, and `model` link for current locale as fallback
          link: offer?.link || fallBackModelLink,
        }
      })

      return modelsFormatted
    },

    specificationsFormatted() {
      const specificationsEnriched = this.models
        .map(({ model: { model, specifications } }) =>
          specifications.groups.map((group) => ({
            ...group,
            productId: model.name,
          })),
        )
        .flat()

      const reviewsEnriched = this.models.map(
        ({ model: { model, reviewsRating } }) => ({
          ...reviewsRating,
          productId: model.name,
        }),
      )

      // `label` is used below as object key
      // eslint-disable-next-line no-unused-vars
      const { label } = this.models[0].model.reviewsRating

      const specificationsAndReviewsByLabel = {
        [label]: reviewsEnriched,
        ...groupBy(specificationsEnriched, 'label'),
      }

      return specificationsAndReviewsByLabel
    },

    chartData() {
      const modelsScores = this.models.map(({ model }) => [
        model.reviewsRating,
        ...model.specifications.groups,
      ])
      const modelsNames = this.models.map(({ model }) => model.model.name)

      return { modelsNames, modelsScores }
    },
  },

  methods: {
    async getModelData(model) {
      const customConfigForModel = this.getCustomModelConfig(model)

      try {
        const { results } = await this.algoliaClient.multipleQueries(
          customConfigForModel,
        )

        return {
          model: results.find((result) => result.index.includes('model'))
            .hits[0],
          offer: results.find((result) => result.index.includes('price'))
            .hits[0],
        }
      } catch (error) {
        logger.error('Error fetching model data from catalog', {
          error,
          modelName: model.compareName,
        })

        return {}
      }
    },

    getCustomModelConfig(model) {
      const { compareName, catalogName } = model

      // We need to update the config with the selected model name
      const indexPriceConfig = this.replaceModelNameInConfig(
        catalogName,
        'price',
      )
      const indexComparisonConfig = this.replaceModelNameInConfig(
        compareName,
        'model',
      )

      return [indexPriceConfig, indexComparisonConfig]
    },

    replaceModelNameInConfig(modelName, index) {
      const configToUpdate = this.comparisonConfig.find((conf) =>
        conf.indexName.includes(index),
      )
      const filtersToUpdate = configToUpdate.params.facetFilters
      const nameToReplace = index === 'price' ? 'CATALOG_NAME' : 'COMPARE_NAME'

      const facetFiltersWithModelName = filtersToUpdate.map((filter) => {
        return filter.includes(nameToReplace)
          ? filter.replace(nameToReplace, modelName)
          : filter
      })

      return {
        indexName: configToUpdate.indexName,
        params: {
          ...configToUpdate.params,
          facetFilters: facetFiltersWithModelName,
        },
      }
    },

    handleModelCtaClick(modelName) {
      trackClick({
        name: `buy_${modelName}`,
      })
    },
  },
}
